'use client';

import { CN_PRODUCT } from '@/components/product/const';
import dynamic from 'next/dynamic';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import PreviewResource from '@/components/previewResource';
import BottomResourceList from '@/components/previewResource/components/BottomResourceList';
import { ResourceTypeEnum, type ResourceListItem } from '@/components/previewResource/index-type';
import { useImagePreloader } from '@/hooks/useImagePreloader';
import { imageUtil } from '@/utils/imageUtil';
import { useSearchParams } from 'next/navigation';
// styles
import { dataExpTagConfig } from '@/components/product/const';
import { ImageShowWrapper } from '@/styles/product';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25217';
import { useParams } from 'next/navigation';
import MainMedia from './mainMedia';
import { getSkuId } from '@/utils/helpers';
type MediaProps = {
  isVideo: boolean;
  targetSrc: string;
  index: number | string;
};

const CDNImage = dynamic(() => import('../cdnImage/cdnImage'), { ssr: false });
const Follow = dynamic(() => import('@/components/product/follow'), { ssr: false });

export default function ImgShowZone({ imagePartList, videoPartList, followState, skuStatus, skuAcId }: any) {
  const imgData = (videoPartList || []).concat(imagePartList || []);
  const [errorStack, setErrorStack] = useState<Record<string, string>>({});
  const [activeThumbnail, setActiveThumbnail] = useState<Record<number | string, MediaProps>>(
    () => {
      const videoSrc = imgData[0]?.['C-P#video&video']?.videoUrl;
      const isVideo = !!videoSrc;
      return {
        0: {
          index: 0,
          isVideo,
          targetSrc: isVideo ? videoSrc : imgData[0]?.['C-P#image&image']?.imageUrl,
        },
      };
    },
  );
  const [mainImageLoaded, setMainImageLoaded] = useState(false); // 是否加载主图
  const searchParams = useSearchParams();
  const skuId = getSkuId(searchParams.get('ref_sku'), skuAcId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMainImageLoaded(true);
    }, 1500); // 异常兜底 避免主图无法正常渲染
    return () => clearTimeout(timer);
  }, []);
  const { sku } = useParams<{ sku: string }>();
  const activeProps = useMemo(() => {
    return {
      show: !errorStack[Object.keys(activeThumbnail)?.[0]],
      ...(Object.values(activeThumbnail)?.[0] || {}),
    };
  }, [activeThumbnail, errorStack]);

  const extractImageUrls = (
    options: {
      width?: number;
      height?: number;
      quality?: number;
    } = {},
  ): string[] => {
    const { width = 1248, height = 1248, quality = 70 } = options;
    const imageUrls: string[] = [];

    imgData.forEach((item: any) => {
      // 提取主图
      const { coverImageUrl } = item['C-P#video&video'] || {};
      const { imageUrl } = item['C-P#image&image'] || {};
      const mainImgSrc = coverImageUrl || imageUrl;

      if (mainImgSrc) {
        const bigImgUrl = imageUtil.concatImgUrlWithoutForceHttps(
          mainImgSrc,
          width,
          height,
          quality,
        );
        imageUrls.push(bigImgUrl);
      }

      // 提取补充图片
      const supplementaryImageList = item['C-P#image&image']?.supplementaryImageList || [];
      supplementaryImageList.forEach((picUrl: string) => {
        const bigImgUrl = imageUtil.concatImgUrlWithoutForceHttps(picUrl, width, height, quality);
        imageUrls.push(bigImgUrl);
      });
    });

    return imageUrls;
  };
  const imageUrls = useMemo(() => {
    return extractImageUrls({
      width: 1248,
      height: 1248,
    });
  }, [imgData]);

  // 使用预加载 Hook
  useImagePreloader(imageUrls, {
    delay: 500,
    priority: 'high',
    enableProgress: false,
  });

  const onImageError = (index: number | string) => {
    // @ts-ignore
    setErrorStack((prevErrorStack) => ({ ...prevErrorStack, [index]: index }));
  };

  const handleMouseEnter = (index: number, isVideo: boolean, targetSrc: string) => {
    setActiveThumbnail({
      [index]: { index, isVideo, targetSrc },
    });

    sendClickTrack.Productdetail_MainPicture_Click({
      mainskuid: sku,
      madiaType: isVideo ? 'video' : 'picture',
      index,
    });
  };

  const mediaContentRef = useRef<HTMLDivElement>(null);
  const [previewState, setPreviewState] = useState(false);
  const resourceList = useMemo(() => {
    const list: ResourceListItem[] = [];
    (videoPartList || []).forEach((item: any) => {
      if (item['C-P#video&video']?.videoUrl) {
        list.push({
          type: ResourceTypeEnum.Video,
          url: item['C-P#video&video'].videoUrl,
          coverImageUrl: item['C-P#video&video']?.coverImageUrl || '',
        });
      }
    });

    (imagePartList || []).forEach((item: any) => {
      if (item['C-P#image&image']?.imageUrl) {
        list.push({
          type: ResourceTypeEnum.Image,
          url: item['C-P#image&image']?.imageUrl,
        });
      }

      (item['C-P#image&image']?.supplementaryImageList || []).forEach((imgUrl: string) => {
        list.push({
          type: ResourceTypeEnum.Image,
          url: imgUrl,
        });
      });
    });

    return list;
  }, [imagePartList, videoPartList]);

  const previewResource = () => {
    setPreviewState(true);

    if (activeProps.isVideo && mediaContentRef.current) {
      const videoEle = mediaContentRef.current.querySelector('video');
      videoEle?.pause();
    }
  };

  const [defaultResourceUrl, setDefaultResourceUrl] = useState<string | undefined>(undefined);
  const changeResourceIndex = (newIndex: number) => {
    const currentInfo = resourceList.find((_, index: number) => index === newIndex);
    setDefaultResourceUrl(currentInfo?.url);
  };

  return (
    <ImageShowWrapper id='white_screen_detection_element'>
      <div className={CN_PRODUCT('thumbnails-list')}>
        {imgData.map((item: any, index: number) => {
          const videoCoverImgSrc = item['C-P#video&video']?.coverImageUrl;
          const videoSourceSrc = item['C-P#video&video']?.videoUrl;

          const isVideo = !!videoCoverImgSrc;

          const imgSrc = videoCoverImgSrc || item['C-P#image&image']?.imageUrl;

          // supplementaryImageList
          const supplementaryImageList = item['C-P#image&image']?.supplementaryImageList;
          return (
            <Fragment key={index}>
              <div
                className={`
                        ${CN_PRODUCT('thumbnail')} 
                        ${activeThumbnail[index] ? 'active' : ''}
                        `}
                onMouseEnter={() =>
                  handleMouseEnter(index, isVideo, isVideo ? videoSourceSrc : imgSrc)
                }
              >
                {isVideo && <div className='player-tag-icon'>Video</div>}
                <CDNImage
                  src={imgSrc}
                  width={112}
                  height={112}
                  alt='thumbnail'
                  onError={() => onImageError(index)}
                />
              </div>
              {supplementaryImageList?.map((picUrl: string, index2: number) => {
                const keyIndex = index + '-' + index2;
                // @ts-ignore
                return (
                  <div
                    className={`${CN_PRODUCT('thumbnail')} ${
                      activeThumbnail[keyIndex] ? 'active' : ''
                    }`}
                    key={keyIndex}
                    // @ts-ignore
                    onMouseEnter={() => handleMouseEnter(keyIndex, false, picUrl)}
                  >
                    <CDNImage
                      src={picUrl}
                      width={112}
                      height={112}
                      alt='thumbnail'
                      onError={() => onImageError(keyIndex)}
                    />
                  </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>
      <div
        className='media-content'
        exptag={'exp|' + EXPOSURE_RECORD.Productdetail_MainPicture_Expo}
        data-exptag-config={dataExpTagConfig}
        data-exptag-json-param={JSON.stringify({
          mainskuid: sku,
          madiaType: activeProps.isVideo ? 'video' : 'picture',
          index: activeProps?.index || 0,
          skuStatus,
          skuid: skuId,
        })}
        ref={mediaContentRef}
        onClick={previewResource}
      >
        <MainMedia
          isVideo={activeProps.isVideo}
          targetSrc={activeProps.targetSrc}
          sku={sku}
          mainImageLoaded={mainImageLoaded}
          setMainImageLoaded={setMainImageLoaded}
        />
        <Follow followState={followState} />
      </div>

      {previewState && (
        <PreviewResource
          open={previewState}
          defaultUrl={defaultResourceUrl ?? activeProps.targetSrc}
          resourceList={resourceList}
          closeHandler={() => {
            setPreviewState(false);
            setDefaultResourceUrl(undefined);
          }}
          bottomSlot={(currentIndex: number) => (
            <BottomResourceList
              resourceList={resourceList}
              currentIndex={currentIndex}
              changeIndexHandler={changeResourceIndex}
            />
          )}
        />
      )}
    </ImageShowWrapper>
  );
}
